import { useCallback, useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

type WregInfoModalCloseMethod = 'close_button' | 'outside_click' | 'escape';

type WregInfoModalImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

type WregInfoModalTitleSize = 'large' | 'compact';

type Props = {
  open: boolean;
  onClose: (method: WregInfoModalCloseMethod) => void;
  titleId: string;
  title: string;
  titleSize?: WregInfoModalTitleSize;
  sub?: string;
  image?: WregInfoModalImage;
  children?: ReactNode;
  closeLabel: string;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const TITLE_CLASS: Record<WregInfoModalTitleSize, string> = {
  large: 'rg-h1',
  compact: 'rg-modal-title',
};

const WregInfoModal = ({
  open,
  onClose,
  titleId,
  title,
  titleSize = 'large',
  sub,
  image,
  children,
  closeLabel,
}: Props) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const root = document.documentElement;
    const overflow = root.style.overflow;
    root.style.overflow = 'hidden';
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      root.style.overflow = overflow;
      restoreRef.current?.focus?.({ preventScroll: true });
    };
  }, [open]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose('escape');
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = event.currentTarget;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return (
    <div className="rg-modal-back show" onClick={() => onClose('outside_click')}>
      <div
        ref={panelRef}
        className="rg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <h2 className={TITLE_CLASS[titleSize]} id={titleId}>
          {title}
        </h2>
        <div className="rg-modal-scroll">
          {sub && <p className="rg-sub">{sub}</p>}
          {image && (
            <img
              className="rg-mbi-img"
              src={image.src}
              width={image.width}
              height={image.height}
              alt={image.alt}
            />
          )}
          {children}
        </div>
        <button
          type="button"
          className="dsb dsb-secondary dsb-m dsb-block wreg-modal-close"
          onClick={() => onClose('close_button')}
        >
          {closeLabel}
        </button>
      </div>
    </div>
  );
};

export { WregInfoModal };
export type { WregInfoModalCloseMethod, WregInfoModalTitleSize };
